import React, { type FC, type FocusEvent, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Box, Divider, Popover, type SxProps, type Theme, Tooltip, Typography } from '@mui/material'
import {
    type Active,
    type Announcements,
    closestCenter,
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    type Modifier,
    type Over,
    PointerSensor,
    type UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { assert } from '../../../../utils'

import {
    applyColumnView,
    type ColumnView,
    ConnectwareError,
    ConnectwareErrorType,
    defaultColumnView,
    isDefaultColumnView,
    moveColumn,
    orderColumns,
    toggleColumnHidden,
    toggleColumnPinned,
    Translation,
} from '../../../../domain'

import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { DragHandleIcon, MaximizeIcon, PinFilledIcon, PinOutlineIcon, SettingsOutlineIcon, UpdateIcon } from '../Branding'
import { Button, IconButton } from '../Button'
import { useAutoFitColumns } from './AutoFit'
import { TableCheckbox } from './Checkbox'
import { ColumnLabel, useColumnLabelText } from './Label'
import { useAllColumnNames, useColumn, useShowsRows } from './State'

const PANEL_WIDTH = 280
const SECTION_LABEL_HEIGHT = 24
const ROW_HEIGHT = 32
const HANDLE_ICON_SIZE = 16

/** Marks the elements of a row that take the focus, so a change that moves or remounts the row can give it back */
const CONTROL_ATTRIBUTE = 'data-column-control'
type ControlKind = 'handle' | 'visible' | 'pin'
const controlAttributes = (name: string, kind: ControlKind): Readonly<Record<typeof CONTROL_ATTRIBUTE, string>> => ({
    [CONTROL_ATTRIBUTE]: `${name}:${kind}`,
})

const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 })

const paperStyle: SxProps<Theme> = { width: PANEL_WIDTH, p: 2, borderRadius: 2, border: 1, borderColor: 'grey.300', boxShadow: 4 }

const sectionLabelStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    height: SECTION_LABEL_HEIGHT,
    px: 2,
    fontWeight: 'fontWeightMedium',
    color: 'grey.500',
}

const dividerStyle: SxProps<Theme> = { my: 1.5, mx: 1 }

const rowStyle: SxProps<Theme> = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    height: ROW_HEIGHT,
    px: 2,
    borderRadius: 1,
    typography: 'body2',
    color: 'grey.900',
    bgcolor: 'common.white',
    '&:hover': { bgcolor: 'grey.100' },
}
/** A hidden column's label goes quiet, so a section reads as shown and hidden columns without comparing checkboxes */
const hiddenRowStyle: SxProps<Theme> = { color: 'grey.500' }
const lockedRowStyle: SxProps<Theme> = { color: 'grey.400' }
const draggedRowStyle: SxProps<Theme> = { zIndex: 1, bgcolor: 'grey.100', boxShadow: 3 }

const handleStyle: SxProps<Theme> = {
    color: 'grey.500',
    cursor: 'grab',
    touchAction: 'none',
    '& svg': { width: HANDLE_ICON_SIZE, height: HANDLE_ICON_SIZE },
}

const labelStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0, cursor: 'pointer' }
const labelTextStyle: SxProps<Theme> = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

const pinStyle: SxProps<Theme> = { color: 'grey.500' }
const pinnedStyle: SxProps<Theme> = { color: 'primary.700', '&:hover': { color: 'primary.700' } }

const actionStyle: SxProps<Theme> = { justifyContent: 'flex-start', px: 2 }

type ColumnChange = (view: ColumnView) => void

/** The column a drag is over, unless it's the dragged column itself, where dropping changes nothing */
const dropTarget = (active: Active, over: Over | null): string | null => (over === null || over.id === active.id ? null : String(over.id))

type RowProps = Readonly<{
    name: string
    names: readonly string[]
    view: ColumnView
    visible: boolean
    locked: boolean
    onChange: ColumnChange
}>

const ColumnRow: FC<RowProps> = ({ name, names, view, visible, locked, onChange }) => {
    const translator = useTranslator()
    const labelText = useColumnLabelText()(name)
    const { label } = useColumn(name)
    const pinned = view.pinned.includes(name)
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: name })

    return (
        <Box
            ref={setNodeRef}
            data-testid={`table-column-view-row-${name}`}
            sx={[rowStyle, !visible && hiddenRowStyle, locked && lockedRowStyle, isDragging && draggedRowStyle]}
            style={{ transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined, transition }}
        >
            <IconButton
                ref={setActivatorNodeRef}
                data-testid={`table-column-view-handle-${name}`}
                {...controlAttributes(name, 'handle')}
                variant="ghost"
                size="small"
                sx={handleStyle}
                aria-label={translator.formatTranslation(Translation.REORDER_COLUMN, { column: labelText })}
                aria-describedby={attributes['aria-describedby']}
                {...listeners}
            >
                <DragHandleIcon />
            </IconButton>
            <Box component="label" sx={labelStyle}>
                <TableCheckbox
                    data-testid={`table-column-view-visible-${name}`}
                    slotProps={{ input: { ...controlAttributes(name, 'visible') } }}
                    checked={visible}
                    disabled={locked}
                    onChange={() => onChange(toggleColumnHidden(names, view, name))}
                />
                <Box component="span" sx={labelTextStyle}>
                    {label === undefined ? labelText : <ColumnLabel name={name} />}
                </Box>
            </Box>
            <IconButton
                data-testid={`table-column-view-pin-${name}`}
                {...controlAttributes(name, 'pin')}
                variant="ghost"
                size="small"
                sx={pinned ? pinnedStyle : pinStyle}
                aria-label={translator.formatTranslation(Translation.PIN_COLUMN, { column: labelText })}
                aria-pressed={pinned}
                onClick={() => onChange(toggleColumnPinned(names, view, name))}
            >
                {pinned ? <PinFilledIcon /> : <PinOutlineIcon />}
            </IconButton>
        </Box>
    )
}

type SectionProps = Readonly<{
    title: Translation
    columns: readonly string[]
    names: readonly string[]
    view: ColumnView
    visible: readonly string[]
    onChange: ColumnChange
    onDragging: (dragging: boolean) => void
}>

/** Its own drag and drop context, so a column is only ever dropped within its own group */
const Section: FC<SectionProps> = ({ title, columns, names, view, visible, onChange, onDragging }) => {
    const titleId = useId()
    const translator = useTranslator()
    const labelText = useColumnLabelText()
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))

    const announcements = useMemo<Announcements>(() => {
        const text = (id: UniqueIdentifier): string => labelText(String(id))
        return {
            onDragStart: ({ active }) => translator.formatTranslation(Translation.COLUMN_DRAG_START, { column: text(active.id) }),
            onDragOver: ({ active, over }) => {
                const target = dropTarget(active, over)
                return target === null
                    ? undefined
                    : translator.formatTranslation(Translation.COLUMN_DRAG_OVER, { column: text(active.id), target: text(target) })
            },
            onDragEnd: ({ active, over }) => {
                const target = dropTarget(active, over)
                return target === null
                    ? translator.formatTranslation(Translation.COLUMN_DRAG_CANCEL, { column: text(active.id) })
                    : translator.formatTranslation(Translation.COLUMN_DRAG_END, { column: text(active.id), target: text(target) })
            },
            onDragCancel: ({ active }) => translator.formatTranslation(Translation.COLUMN_DRAG_CANCEL, { column: text(active.id) }),
        }
    }, [translator, labelText])

    const onDragEnd = ({ active, over }: DragEndEvent): void => {
        onDragging(false)
        const target = dropTarget(active, over)
        if (target !== null) {
            onChange(moveColumn(names, view, String(active.id), target))
        }
    }

    return (
        <Box role="group" aria-labelledby={titleId} data-testid={`table-column-view-section-${title.toLowerCase()}`}>
            <Typography id={titleId} variant="caption" component="p" sx={sectionLabelStyle}>
                <FormattedTranslation id={title} />
            </Typography>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                accessibility={{ announcements, screenReaderInstructions: { draggable: translator.formatTranslation(Translation.COLUMN_DRAG_INSTRUCTIONS) } }}
                onDragStart={() => onDragging(true)}
                onDragCancel={() => onDragging(false)}
                onDragEnd={onDragEnd}
            >
                <SortableContext items={[...columns]} strategy={verticalListSortingStrategy}>
                    {columns.map((name) => (
                        <ColumnRow
                            key={name}
                            name={name}
                            names={names}
                            view={view}
                            visible={visible.includes(name)}
                            locked={visible.length === 1 && visible.includes(name)}
                            onChange={onChange}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </Box>
    )
}

type Props = Readonly<{ view: ColumnView, onChange: ColumnChange }>

/**
 * The gear that opens the column view panel: show or hide, pin and reorder the columns, fit them to their content, or reset them.
 * The view is controlled; every change is reported through `onChange`
 */
export const ColumnViewMenu: FC<Props> = ({ view, onChange }) => {
    const translator = useTranslator()
    const buttonRef = useRef<HTMLButtonElement>(null)
    const names = useAllColumnNames()
    const [open, setOpen] = useState(false)
    const [dragging, setDragging] = useState(false)
    const autoFit = useAutoFitColumns(onChange)
    const showsRows = useShowsRows()
    const paperRef = useRef<HTMLDivElement>(null)
    /** A change can move a row, or remount it in the other section, which loses the focus: it goes back to the same control */
    const focusedControl = useRef<string | null>(null)

    useLayoutEffect(() => {
        const paper = paperRef.current
        const active = document.activeElement
        if (focusedControl.current === null || paper === null || (active !== null && active !== document.body && active !== paper)) {
            return
        }

        const control = Array.from(paper.querySelectorAll<HTMLElement>(`[${CONTROL_ATTRIBUTE}]`)).find(
            (element) => element.getAttribute(CONTROL_ATTRIBUTE) === focusedControl.current
        )
        control?.focus()
    }, [view])

    const close = (): void => {
        focusedControl.current = null
        setOpen(false)
    }

    const label = translator.formatTranslation(Translation.CONFIGURE_COLUMNS)
    const ordered = orderColumns(names, view)
    const visible = applyColumnView(names, view)
    const sections: readonly [Translation, readonly string[]][] = [
        [Translation.PINNED, ordered.filter((name) => view.pinned.includes(name))],
        [Translation.COLUMNS, ordered.filter((name) => !view.pinned.includes(name))],
    ]
    const nonEmptySections = sections.filter(([, columns]) => columns.length > 0)

    return (
        <>
            <Tooltip title={label} placement="left">
                <IconButton
                    ref={buttonRef}
                    data-testid="table-column-view-button"
                    variant="ghost"
                    size="medium"
                    aria-label={label}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    onClick={() => setOpen(true)}
                >
                    <SettingsOutlineIcon />
                </IconButton>
            </Tooltip>
            <Popover
                onFocus={(e: FocusEvent<HTMLElement>) => {
                    focusedControl.current = e.target.getAttribute(CONTROL_ATTRIBUTE)
                }}
                data-testid="table-column-view-menu"
                open={open}
                anchorEl={buttonRef.current}
                onClose={close}
                disableEscapeKeyDown={dragging}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        ref: paperRef,
                        role: 'dialog',
                        'aria-modal': true,
                        'aria-label': label,
                        sx: paperStyle,
                    },
                }}
            >
                {nonEmptySections.map(([title, columns], index) => (
                    <React.Fragment key={title}>
                        {index > 0 && <Divider sx={dividerStyle} />}
                        <Section title={title} columns={columns} names={names} view={view} visible={visible} onChange={onChange} onDragging={setDragging} />
                    </React.Fragment>
                ))}
                <Divider sx={dividerStyle} />
                <Button
                    data-testid="table-column-view-auto-fit"
                    variant="ghost"
                    size="medium"
                    fullWidth
                    sx={actionStyle}
                    startIcon={<MaximizeIcon />}
                    /**
                     * The last column absorbs the remaining width, so a single one has nothing to fit.
                     * Without data rows only the headers would be measured, shrinking the columns to them
                     */
                    disabled={visible.length < 2 || !showsRows}
                    onClick={() => {
                        const button = buttonRef.current
                        assert(button, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'The column view menu must be opened from its button'))
                        close()
                        autoFit(button, visible.slice(0, -1))
                    }}
                >
                    <FormattedTranslation id={Translation.AUTO_FIT_COLUMNS} />
                </Button>
                <Button
                    data-testid="table-column-view-reset"
                    variant="ghost"
                    size="medium"
                    fullWidth
                    sx={actionStyle}
                    startIcon={<UpdateIcon />}
                    disabled={isDefaultColumnView(names, view)}
                    onClick={() => {
                        close()
                        onChange(defaultColumnView)
                    }}
                >
                    <FormattedTranslation id={Translation.RESET_VIEW} />
                </Button>
            </Popover>
        </>
    )
}
